import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Mail, MessageSquare, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/settings`;
const err = (e, fb) => e.response?.data?.detail || fb;

const WELCOME = {
  path: 'helpconnect-welcome', noun: 'Welcome wording', title: 'HELPConnect Welcome Email and Text',
  description: "Sent automatically the first time a participant is enrolled in a HELPConnect course or module, with the steps to set up their account. Only the wording changes here; when and to whom it's sent stays the same.",
  note: 'The setup link is always added, even if you remove it.',
};
const DEACTIVATED = {
  path: 'helpconnect-deactivated', noun: 'Access ended wording', title: 'HELPConnect Access Deactivated Email and Text',
  description: "Sent automatically when a participant's HELPConnect access ends after their case is closed: right away for unsuccessful exits, or 30 days after a successful completion. Their progress and certificates are kept.",
  note: '',
};
const ENDING = {
  path: 'helpconnect-access-ending', noun: 'Access ending reminder wording', title: 'HELPConnect Access Ending Reminder Email and Text',
  description: "Sent automatically once, 7 days before a participant's 30-day HELPConnect access ends after a successful case closure.",
  note: '',
};

const FieldList = ({ fields, prefix, note }) => (
  <div className="rounded-lg border p-3 space-y-1 text-xs" data-testid={`${prefix}-fields`}>
    <p className="font-semibold text-sm mb-1">Available fields</p>
    {fields.map((f) => (
      <p key={f.token}><code className="bg-muted px-1 rounded">{f.token}</code> <span className="text-muted-foreground">{f.label}</span></p>
    ))}
    {note && <p className="text-muted-foreground pt-1">{note}</p>}
  </div>
);

const Preview = ({ title, children, testid }) => (
  <div className="rounded-lg border bg-muted/30 p-3 text-sm space-y-1" data-testid={testid}>
    <p className="text-xs font-semibold uppercase text-muted-foreground">{title}</p>
    {children}
  </div>
);

const HelpConnectTemplateCard = ({ cfg }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === 'system_admin';
  const API = `${BASE}/${cfg.path}`;
  const t = (s) => `${cfg.path}-${s}`;

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(API).then((r) => { setData(r.data); setForm({ subject: r.data.subject, email_body: r.data.email_body, sms: r.data.sms }); })
      .catch((e) => toast.error(err(e, `Failed to load ${cfg.noun.toLowerCase()}`)));
  }, [isAdmin, API, cfg.noun]);

  useEffect(() => {
    if (!form) return undefined;
    const tm = setTimeout(() => {
      axios.post(`${API}/preview`, form).then((r) => setPreview(r.data)).catch(() => {});
    }, 400);
    return () => clearTimeout(tm);
  }, [form, API]);

  if (!isAdmin) return null;
  if (!form) return <Card className="card-shadow"><CardContent className="py-6"><Loader2 className="w-5 h-5 animate-spin" /></CardContent></Card>;

  const save = async () => {
    setSaving(true);
    try { setData((await axios.put(API, form)).data); toast.success(`${cfg.noun} saved`); }
    catch (e) { toast.error(err(e, `Failed to save ${cfg.noun.toLowerCase()}`)); }
    finally { setSaving(false); }
  };
  const restoreEmail = () => setForm({ ...form, subject: data.defaults.subject, email_body: data.defaults.email_body });
  const restoreSms = () => setForm({ ...form, sms: data.defaults.sms });

  return (
    <Card className="card-shadow" data-testid={t('card')}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5" />{cfg.title}</CardTitle>
        <CardDescription>{cfg.description}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6 lg:grid-cols-[1fr_260px]">
        <div className="space-y-6">
          <div className="space-y-2" data-testid={t('email-box')}>
            <div className="flex items-center justify-between">
              <p className="font-semibold flex items-center gap-2"><Mail className="w-4 h-4" />Email</p>
              <Button variant="ghost" size="sm" onClick={restoreEmail} data-testid={t('restore-email-btn')}><RotateCcw className="w-4 h-4 mr-1" />Restore original wording</Button>
            </div>
            <label className="text-sm font-medium">Subject line</label>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid={t('subject-input')} />
            <label className="text-sm font-medium">Message</label>
            <Textarea rows={9} value={form.email_body} onChange={(e) => setForm({ ...form, email_body: e.target.value })} data-testid={t('email-input')} />
            {preview && (
              <Preview title="Email preview (sample details)" testid={t('email-preview')}>
                <p><span className="text-muted-foreground">Subject:</span> <b>{preview.subject}</b></p>
                <div className="bg-white rounded border p-3 [&_p]:mb-2" dangerouslySetInnerHTML={{ __html: preview.html }} />
              </Preview>
            )}
          </div>
          <div className="space-y-2" data-testid={t('sms-box')}>
            <div className="flex items-center justify-between">
              <p className="font-semibold flex items-center gap-2"><MessageSquare className="w-4 h-4" />Text message</p>
              <Button variant="ghost" size="sm" onClick={restoreSms} data-testid={t('restore-sms-btn')}><RotateCcw className="w-4 h-4 mr-1" />Restore original wording</Button>
            </div>
            <Textarea rows={4} value={form.sms} onChange={(e) => setForm({ ...form, sms: e.target.value })} data-testid={t('sms-input')} />
            {preview && (
              <Preview title="Text preview (sample details)" testid={t('sms-preview')}>
                <p className="whitespace-pre-wrap">{preview.sms}</p>
              </Preview>
            )}
          </div>
          <Button onClick={save} disabled={saving} data-testid={t('save-btn')}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save
          </Button>
        </div>
        <FieldList fields={data.fields} prefix={cfg.path} note={cfg.note} />
      </CardContent>
    </Card>
  );
};

export const HelpConnectWelcomeCard = () => <HelpConnectTemplateCard cfg={WELCOME} />;
export const HelpConnectDeactivatedCard = () => <HelpConnectTemplateCard cfg={DEACTIVATED} />;
export const HelpConnectAccessEndingCard = () => <HelpConnectTemplateCard cfg={ENDING} />;
